import { useEffect } from 'react';

/**
 * `alt+↑` / `alt+↓`: step back and forward through whatever the chat is
 * currently presenting in sequence — turns in the transcript, or the questions
 * inside an open prompt. One control for both, so the gesture means the same
 * thing wherever the reader is.
 *
 * Bound on `window`, so callers must scope it themselves: only one stepper
 * should be enabled at a time (an open prompt outranks the transcript behind
 * it), and only on the live agent surface — a page can embed several replays,
 * and each would otherwise bind its own listener.
 *
 * Keystrokes aimed at a text field are left alone: `alt+↑` moves by paragraph
 * in a textarea on macOS, and stealing it from a composer mid-sentence would be
 * worse than not having the shortcut.
 */
export function useStepHotkeys({
  onPrevious,
  onNext,
  enabled = true,
}: {
  onPrevious: () => void;
  onNext: () => void;
  enabled?: boolean;
}): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const handler = (event: KeyboardEvent) => {
      if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      if (isEditable(document.activeElement)) return;

      event.preventDefault();
      if (event.key === 'ArrowUp') onPrevious();
      else onNext();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onNext, onPrevious]);
}

function isEditable(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
